"use client"

import { Head, router, usePage } from "@inertiajs/react"
import { useState } from "react"

import { batyrGroupPhotoBlob as fallbackImage } from "@/assets/images/photos"
import {
  SplitBackgroundEffect,
  SplitImageWrapper,
  SplitQuoteCarousel,
} from "@/components/auth"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Link } from "@/components/ui/link"
import { BatyrLogo, BatyrLogoVariant } from "@/components/ui/logo-batyr"
import { AuthSplitLayout } from "@/layouts/auth/auth-split-layout"
import { signInPath, signUpPath } from "@/routes"
import type { Brief } from "@/types"

// See sessions/new.tsx for detailed explanation of the Form Pattern.
// TL;DR: Uncontrolled inputs + FormData = copy-paste friendly UUI code.

interface PageProps {
  errors: Record<string, string>
  briefs?: Brief[]
  [key: string]: unknown
}

export default function Register() {
  const { errors, briefs } = usePage<PageProps>().props
  const [processing, setProcessing] = useState(false)

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    router.post(
      signUpPath(),
      Object.fromEntries(new FormData(e.currentTarget)),
      {
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <AuthSplitLayout
      sidebar={
        <SplitQuoteCarousel
          briefs={briefs}
          fallback={<SplitImageWrapper src={fallbackImage} />}
        />
      }
    >
      <SplitBackgroundEffect />
      <Head title="Join the herd" />
      <header className="flex flex-col items-center gap-6 text-center">
        <BatyrLogo variant={BatyrLogoVariant.ICON} className="h-12 w-auto" />
        <h1 className="text-display-sm text-primary font-semibold text-balance">
          Join the herd
        </h1>
      </header>

      <form onSubmit={submit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-5">
          <Input
            label="Name"
            type="text"
            name="name"
            placeholder="What should we call you?"
            size="md"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.name}
            errorMessage={errors?.name}
            autoFocus
            autoComplete="name"
          />
          <Input
            label="Email"
            type="email"
            name="email"
            placeholder="you@example.com"
            size="md"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.email}
            errorMessage={errors?.email}
            autoComplete="email"
          />
          <Input
            label="Password"
            type="password"
            name="password"
            size="md"
            placeholder="Create a password"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.password}
            errorMessage={errors?.password}
            autoComplete="new-password"
            hint="At least 8 characters"
          />
          <Input
            label="Confirm password"
            type="password"
            name="password_confirmation"
            size="md"
            placeholder="Confirm your password"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.password_confirmation}
            errorMessage={errors?.password_confirmation}
            autoComplete="new-password"
          />
        </div>

        <div className="flex flex-col gap-4">
          <Button
            type="submit"
            size="lg"
            isDisabled={processing}
            isLoading={processing}
            showTextWhileLoading
          >
            Create my account
          </Button>
        </div>
      </form>

      <div className="flex justify-center gap-1 text-center">
        <span className="text-secondary text-sm">Already joined up?</span>
        <Link
          href={signInPath()}
          className="text-primary hover:text-secondary text-sm font-semibold"
        >
          Sign in
        </Link>
      </div>
    </AuthSplitLayout>
  )
}
